import type { OwnGutter } from "@/lib/width-group/width-group-registry";
import type { TextMeasurer } from "@/utils/text";
import { AxisSide, type AxisSpec } from "../../ChartSpec";
import { BAND_ABOVE_RESERVE_PX, BAND_HEIGHT_PX, BAND_STRIP_EXTRA_PX, PLOT_TO_BAND_GAP_PX } from "../band/band-geometry";
import { type AxisScale, formatTickLabel } from "./axis-layout";
import { TOP_TICK_LABEL_OVERHANG_PX, X_AXIS_BAND_PX, Y_AXIS_BASE_OFFSET_PX, Y_TICK_FONT } from "./plot-geometry";

/** At most one right y-axis shows labels; others stay in the spec but hide labels so they don't overlap. */
const MAX_VISIBLE_RIGHT_AXES = 1;

const TICK_LABEL_SAFETY_PX = 0;

/** Insets for the plot area — used by ECharts grid and the chrome overlay. */
export type PlotLayout = {
    gutters: { left: number; right: number };
    /** Time labels and pill row at the bottom. A band below sits between this and plotBottomInsetPx. */
    xAxisBandPx: number;
    /** Everything under the plot: the time strip, a band below it, and the gap over the band. */
    plotBottomInsetPx: number;
    plotTopPx: number;
};

/**
 * Axes that show labels on this side — the ones we measure gutter width for.
 * Left: only the active axis. Right: at most MAX_VISIBLE_RIGHT_AXES (first in order).
 * Keep the same rule wherever axis labels are shown/hidden in ECharts options
 * (build-time-panel-options `isVisible` on the panel-wire layer).
 */
function visibleAxesForSide(axes: AxisSpec[], side: AxisSide, activeLeftAxisId: string | undefined): AxisSpec[] {
    const sideAxes = axes.filter((axis) => axis.side === side);
    if (side === AxisSide.Left) {
        return sideAxes.filter((axis) => axis.id === activeLeftAxisId);
    }
    return sideAxes.slice(0, MAX_VISIBLE_RIGHT_AXES);
}

function tickLabelRequirementPx(scale: AxisScale | undefined, measure: TextMeasurer): number {
    if (!scale) return 0;

    const visibleTicks = scale.ticks.filter((tick) => tick <= scale.maxVisibleTick);
    const widestLabelWidth = Math.max(
        ...visibleTicks.map((tick) => measure(formatTickLabel(tick, scale.step), Y_TICK_FONT)),
    );
    // Axis line sits Y_AXIS_BASE_OFFSET_PX inside the plot edge; labels sit outside that line.
    return Y_AXIS_BASE_OFFSET_PX + Math.ceil(widestLabelWidth) + TICK_LABEL_SAFETY_PX;
}

function gutterForSide(
    axes: AxisSpec[],
    side: AxisSide,
    activeLeftAxisId: string | undefined,
    scalesByAxis: Map<string, AxisScale>,
    measure: TextMeasurer,
): number {
    const visibleAxes = visibleAxesForSide(axes, side, activeLeftAxisId);
    // No labels on this side, so the plot runs flush to the card edge.
    if (visibleAxes.length === 0) return 0;

    return Math.max(...visibleAxes.map((axis) => tickLabelRequirementPx(scalesByAxis.get(axis.id), measure)));
}

export function computeGutterRequirement(input: {
    axes: AxisSpec[];
    activeLeftAxisId: string | undefined;
    scalesByAxis: Map<string, AxisScale>;
    measure: TextMeasurer;
}): OwnGutter {
    const { axes, activeLeftAxisId, scalesByAxis, measure } = input;
    return {
        left: gutterForSide(axes, AxisSide.Left, activeLeftAxisId, scalesByAxis, measure),
        right: gutterForSide(axes, AxisSide.Right, activeLeftAxisId, scalesByAxis, measure),
    };
}

type PlotVerticalInsets = Omit<PlotLayout, "gutters">;

/** The plot's top and bottom insets. Independent of the gutters, so tick sizing can use them before the gutter is known. */
export function derivePlotVerticalInsets(bandPosition?: "above" | "below"): PlotVerticalInsets {
    const below = bandPosition === "below";
    const xAxisBandPx = X_AXIS_BAND_PX + (below ? BAND_STRIP_EXTRA_PX : 0);
    return {
        xAxisBandPx,
        plotBottomInsetPx: xAxisBandPx + (below ? BAND_HEIGHT_PX + PLOT_TO_BAND_GAP_PX : 0),
        // A band above already leaves the top label its room.
        plotTopPx: bandPosition === "above" ? BAND_ABOVE_RESERVE_PX + BAND_HEIGHT_PX : TOP_TICK_LABEL_OVERHANG_PX,
    };
}

export function bandExtraHeightPx(bandPosition?: "above" | "below"): number {
    const withBand = derivePlotVerticalInsets(bandPosition);
    const withoutBand = derivePlotVerticalInsets();
    return withBand.plotTopPx + withBand.plotBottomInsetPx - (withoutBand.plotTopPx + withoutBand.plotBottomInsetPx);
}

/** Turn the stack's shared gutter into this panel's plot layout. Ignores panel size so resizing doesn't create a new layout and re-push ECharts options. */
export function derivePlotLayout(input: {
    sharedGutter: { left: number; right: number };
    bandPosition?: "above" | "below";
}): PlotLayout {
    return { gutters: input.sharedGutter, ...derivePlotVerticalInsets(input.bandPosition) };
}
